import { useSnackbar } from "notistack";
import { FC, useEffect, useRef, useState } from "react";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";

interface AdminIccUsers {
    data: AdminUsersData;
    error: IError;
}
interface AdminUsersData {
    users: AdminUser[];
}
export interface AdminUser {
    id: number;
    username: string;
    alitSystems: number;
    iccGeneral: number;
    mobile: number;
    billing: number;
    xero: number;
    other: number;
}

interface DeleteAdminUserRequest {
    id: number;
}

interface DeleteAdminUserResponse {
    data: DeleteAdminUserData;
    error: IError;
}
interface DeleteAdminUserData {
    status: string;
}

interface ViewAdminUsersProsp {
    editUser: (user: AdminUser) => void;
}

const ViewAdminUsers: FC<ViewAdminUsersProsp> = ({ editUser }) => {
    const columns = [
        { field: 'id', headerName: 'ID', width: 10 },
        { field: 'username', headerName: 'Username', width: 200 },
        { field: 'alitSystems', headerName: 'ALIT systems', width: 150 },
        { field: 'iccGeneral', headerName: 'ICC General', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'billing', headerName: 'Billing', width: 150 },
        { field: 'xero', headerName: 'Xero', width: 150 },
        { field: 'other', headerName: 'Other', width: 150 },
        {
            field: 'edit', headerName: 'Edit user', width: 80,
            renderCell: (param: any) => (
                <IconButton onClick={() => { editUser(param.row) }}>
                    <EditIcon />
                </IconButton>
            )
        },
        {
            field: 'delete', headerName: 'Delete user', width: 100,
            renderCell: (param: any) => (
                <IconButton onClick={() => { AskToDeleteUser(param.row) }}>
                    <DeleteForeverIcon htmlColor="red" />
                </IconButton>
            )
        }
    ];

    const { enqueueSnackbar } = useSnackbar();

    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 50,
        page: 0,
    });
    const [users, setUsers] = useState<AdminUser[]>([]);

    const userToDelete = useRef<AdminUser | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);

    useEffect(() => {
        GetAdminUsers();
    }, []);

    const GetAdminUsers = async () => {
        try {
            const response: AdminIccUsers = await api.get("api/Admin/GetAdminUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AskToDeleteUser(user: AdminUser) {
        userToDelete.current = user;
        setDeleteConfirmation(true);
    }

    function closeDeleteConfirmation() {
        setDeleteConfirmation(false);
    }

    const DeleteUser = async () => {
        if (userToDelete.current == null)
            return;

        var body: DeleteAdminUserRequest = {
            id: userToDelete.current.id
        }

        try {
            const response: DeleteAdminUserResponse = await api.post("api/Admin/DeleteAdminUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Deleted!", { variant: "success" });

                // get new users from db
                GetAdminUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const headerHeight: number = 100;
    function GetGridHeight() {
        return window.innerHeight - headerHeight;
    }

    return (
        <>
            <DataGrid
                sx={{ height: GetGridHeight(), marginTop: "-15px", marginLeft: "-15px", marginRight: "-15px" }}
                rows={users}
                columns={columns}
                pagination
                pageSizeOptions={[10, 15, 20, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 100 },
                    },
                }} />

            <Dialog
                open={deleteConfirmation}
                onClose={closeDeleteConfirmation}>
                <DialogTitle>
                    {"Admin user delete confirmation"}
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to remove this user permanently?
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteConfirmation}>Cancel</Button>
                    <Button color="error" onClick={() => {
                        closeDeleteConfirmation();
                        DeleteUser();
                    }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog >

        </>
    )
}

export default ViewAdminUsers;